/**
 * Pure, dependency-free secret redactor for tool output.
 *
 * Applied in the PostToolUse hook before forwarding `tool_result` events to
 * the API and before buffering into `pendingToolOutputs`. Defense-in-depth
 * only — server-side must still treat tool output as potentially tainted.
 *
 * Patterns are intentionally conservative: we prefer false negatives over
 * false positives because agents rely on reading tool output to do work.
 * Commit SHAs (`[a-f0-9]{7,40}`) are deliberately NOT redacted.
 */

const REDACTED = "<redacted>";

const BEARER_RE = /\b(Bearer\s+)[A-Za-z0-9_\-.]{20,}/g;

const VENDOR_KEY_RE =
  /\b(?:sk-[a-zA-Z0-9_-]{20,}|ghp_[A-Za-z0-9]{36}|github_pat_[A-Za-z0-9_]{22,}|xoxb-[A-Za-z0-9-]+|xai-[A-Za-z0-9-]{20,})\b/g;

const AWS_ACCESS_KEY_RE = /\bAKIA[0-9A-Z]{16}\b/g;

// JWTs: three base64url segments separated by dots, starting with the
// ubiquitous `eyJ` header prefix.
const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;

// KEY=VALUE lines where KEY looks like a secret name. Matches optional
// `export ` prefix and quoted or bare values. Captures KEY separately so we
// preserve it in the replacement.
const ENV_SECRET_RE =
  /^(\s*(?:export\s+)?([A-Z][A-Z0-9_]*(?:TOKEN|SECRET|KEY|PASSWORD|PASS|CREDENTIAL|CREDENTIALS)[A-Z0-9_]*)\s*=\s*)['"]?([^\s'"]+)['"]?/gm;

const COOKIE_HEADER_RE = /(Cookie:\s*)[^\r\n]+/gi;

// Basic-auth URLs: keep the user visible, mask the password.
const BASIC_AUTH_URL_RE = /(https?:\/\/)([^:@\s/]+):([^@\s]+)@/g;

// AWS secret access keys don't have a distinctive prefix. Apply only on
// lines that mention "secret" / "aws_secret" to avoid matching commit SHAs,
// base64 payloads, etc.
const AWS_SECRET_LINE_RE = /^.*(?:secret|aws_secret).*$/gim;
const AWS_SECRET_VALUE_RE = /\b([A-Za-z0-9/+=]{40})\b/g;

// Strip prompt-injection attempts from tool output: we observed `<system-reminder>`
// blocks being smuggled through bash output during discovery of this pipeline.
const SYSTEM_REMINDER_RE = /<system-reminder>[\s\S]*?<\/system-reminder>/gi;

export interface RedactResult {
  output: string;
  redacted: number;
}

export function redact(input: string): RedactResult {
  if (!input) return { output: input, redacted: 0 };

  let count = 0;
  let output = input;

  output = output.replace(SYSTEM_REMINDER_RE, () => {
    count++;
    return "<!-- stripped injection -->";
  });

  output = output.replace(BEARER_RE, (_match, prefix: string) => {
    count++;
    return `${prefix}${REDACTED}`;
  });

  output = output.replace(VENDOR_KEY_RE, () => {
    count++;
    return REDACTED;
  });

  output = output.replace(AWS_ACCESS_KEY_RE, () => {
    count++;
    return REDACTED;
  });

  output = output.replace(JWT_RE, () => {
    count++;
    return REDACTED;
  });

  output = output.replace(AWS_SECRET_LINE_RE, (line) =>
    line.replace(AWS_SECRET_VALUE_RE, (match) => {
      // Skip pure hex (commit SHAs, etc.) — AWS secrets use the full
      // base64 alphabet, so a match that's all-hex is almost certainly
      // not a secret.
      if (/^[a-f0-9]+$/i.test(match)) return match;
      count++;
      return REDACTED;
    }),
  );

  output = output.replace(ENV_SECRET_RE, (_match, prefix: string, _key: string, _value: string) => {
    count++;
    return `${prefix}${REDACTED}`;
  });

  output = output.replace(COOKIE_HEADER_RE, (_match, prefix: string) => {
    count++;
    return `${prefix}${REDACTED}`;
  });

  output = output.replace(BASIC_AUTH_URL_RE, (_match, scheme: string, user: string) => {
    count++;
    return `${scheme}${user}:${REDACTED}@`;
  });

  return { output, redacted: count };
}
